/**
 * columnVisibility.ts — the rules behind `TableColumnPicker`'s visible set.
 *
 * Kept out of the component because this is where the behaviour actually is,
 * and a component's behaviour is only worth as much as the tests that reach
 * it: flowbite's `Dropdown` opens through a `requestAnimationFrame` debounce
 * that never fires under jsdom (its rAF timestamp and `performance.now()` run
 * on different clock origins, so the elapsed time reads negative and the open
 * reschedules forever), which puts everything inside the open menu out of a
 * test's reach. These functions are pure, so they are not.
 */

import type { ColumnOption } from '../AppTypes';

/**
 * Shows or hides one optional column.
 *
 * The result is rebuilt from `columns` rather than appended to, so the visible
 * set always carries the table's own column order — otherwise columns would
 * appear in the order the reader happened to tick them and a header would
 * drift away from the cells beneath it. Keys that are not offered by
 * `columns` are dropped for the same reason: a stored set can outlive the
 * column that put a key in it.
 *
 * @param columns - Every column the table offers, in the order it renders them.
 * @param visible - The currently shown keys.
 * @param key     - The column to flip.
 * @returns The new visible set, in column order.
 */
export function toggleColumn(columns: ColumnOption[], visible: string[], key: string): string[] {
	const isShown = visible.includes(key);
	return columns
		.map((column) => column.key)
		.filter((candidate) => (candidate === key ? !isShown : visible.includes(candidate)));
}

/**
 * Whether a visible set is the table's default arrangement.
 *
 * Order matters as much as membership, since both come from the same column
 * list — two sets holding the same keys in a different order would render
 * different tables.
 *
 * @param visible  - The keys currently shown.
 * @param defaults - The keys shown when nothing has been changed.
 * @returns `true` when the table is in its default shape.
 */
export function isDefaultColumns(visible: string[], defaults: string[]): boolean {
	return visible.length === defaults.length && visible.every((key, index) => key === defaults[index]);
}

/**
 * Decides whether an active sort has to be abandoned after a column change.
 *
 * Hiding the column a table is ordered by would leave it sorted by something
 * the reader can no longer see. Sorts on the permanent columns are never
 * affected, since those cannot be hidden.
 *
 * @param columns - Every optional column the table offers.
 * @param visible - The keys that will be shown after the change.
 * @param sortBy  - The active sort key, or `''` for the server default.
 * @returns `true` when the caller should fall back to the default ordering.
 */
export function sortLostWithColumn(columns: ColumnOption[], visible: string[], sortBy: string): boolean {
	/* An empty key is already the default ordering, and permanent columns stay put. */
	if (!sortBy || !columns.some((column) => column.key === sortBy)) return false;
	return !visible.includes(sortBy);
}
